/*
 * Make My Day header search — snippets/header-search.liquid.
 *
 * The theme's <header-search> (a DialogElement) and <predictive-search> do the opening and the live results. This adds:
 * - focus on the input as soon as the panel opens (the theme only does it through its focus trap, after the animation,
 *   and not at all on touch devices);
 * - the "empty state" shown while the input is empty: recently viewed products (fetched from
 *   sections/mm-search-recently-viewed.liquid with the ids stored by sections/main-product.liquid) and the merchant's
 *   suggested products, rendered in Liquid;
 * - the clear (×) button of the input, and a clean form when the panel is closed.
 */
(() => {
  const STORAGE_KEY = 'theme:recently-viewed-products';

  const readRecentIds = () => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    } catch (error) {
      return [];
    }
  };

  const init = (root) => {
    const form = root.querySelector('form');
    const input = form && form.elements['q'];

    if (!input) {
      return;
    }

    const clearButton = root.querySelector('[data-mm-search-clear]');
    const emptyState = root.querySelector('[data-mm-search-empty]');
    const recentGroup = root.querySelector('[data-mm-search-recent]');
    const recentList = root.querySelector('[data-mm-search-recent-list]');
    let recentLoaded = false;

    const focusInput = () => input.focus({ preventScroll: true });

    // Show the clear button and hide the empty state as soon as there is a query
    const sync = () => {
      const hasQuery = input.value.trim() !== '';

      if (clearButton) {
        clearButton.hidden = !hasQuery;
      }

      if (emptyState) {
        emptyState.hidden = hasQuery;
      }
    };

    const loadRecentlyViewed = async () => {
      if (recentLoaded || !recentGroup || !recentList) {
        return;
      }

      recentLoaded = true;

      const ids = readRecentIds().slice(0, parseInt(recentGroup.dataset.count || '4', 10));

      if (ids.length === 0) {
        return;
      }

      const query = ids.map((id) => `id:${id}`).join(' OR ');
      const url = `${window.Shopify.routes.root}search?type=product&q=${encodeURIComponent(query)}&section_id=mm-search-recently-viewed`;

      try {
        const response = await fetch(url, { priority: 'low' });
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const results = doc.querySelector('[data-mm-search-recent-results]');

        if (!results || results.childElementCount === 0) {
          return;
        }

        recentList.replaceChildren(...document.importNode(results, true).children);
        recentGroup.hidden = false;
      } catch (error) {
        // The panel simply keeps the suggested products
      }
    };

    root.addEventListener('dialog:before-show', () => {
      loadRecentlyViewed();
      focusInput();
    });

    root.addEventListener('dialog:after-show', focusInput);

    // Next opening starts from a blank search (the theme's reset handler also clears the results)
    root.addEventListener('dialog:after-hide', () => form.reset());

    input.addEventListener('input', sync);
    form.addEventListener('reset', () => requestAnimationFrame(sync));

    const clearRecent = root.querySelector('[data-mm-search-recent-clear]');

    if (clearRecent) {
      clearRecent.addEventListener('click', () => {
        try {
          localStorage.removeItem(STORAGE_KEY);
        } catch (error) {
          // Storage unavailable (private mode): only hide the group
        }

        recentGroup.hidden = true;
        recentList.replaceChildren();
        focusInput();
      });
    }

    sync();
  };

  const boot = () => document.querySelectorAll('header-search').forEach(init);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
